/**
 * Конфиг колонок таблицы аккаунтов + персист выбранных колонок в localStorage.
 * У каждого юзера — свои настройки на каждый экран (ключ содержит userId
 * и tableKey), не сбрасываются между сессиями.
 */
import type { Account } from '@wml-insta/shared';

export type ColumnType = 'text' | 'number' | 'bool' | 'date' | 'datetime' | 'status' | 'model' | 'phone' | 'secret' | 'link';

export interface ColumnDef {
  key: string;
  label: string;
  type: ColumnType;
  /** Поле Account, откуда брать значение (и куда патчить). */
  field: keyof Account;
  editable: boolean;
  adminOnly?: boolean;
  /** Показана по умолчанию, если юзер ещё ничего не настраивал. */
  defaultOn: boolean;
  /** Колонка имеет смысл только в архиве (там же включена по умолчанию). */
  onlyArchive?: boolean;
  width?: number;
}

export const ACCOUNT_COLUMNS: ColumnDef[] = [
  { key: 'containerId', label: 'Контейнер', type: 'number', field: 'containerId', editable: true, defaultOn: true, width: 74 },
  { key: 'username', label: 'Юзернейм', type: 'text', field: 'username', editable: true, defaultOn: true, width: 170 },
  { key: 'note', label: 'Комментарий', type: 'text', field: 'note', editable: true, defaultOn: true, width: 180 },
  { key: 'phone', label: 'Телефон', type: 'phone', field: 'phoneId', editable: true, defaultOn: true, width: 110 },
  { key: 'model', label: 'Модель', type: 'model', field: 'modelId', editable: true, defaultOn: true, width: 120 },
  { key: 'status', label: 'Статус', type: 'status', field: 'status', editable: true, defaultOn: true, width: 130 },
  { key: 'country', label: 'Страна', type: 'text', field: 'country', editable: true, defaultOn: true, width: 96 }, // на русском — по ней берётся координата
  { key: 'language', label: 'Язык', type: 'text', field: 'language', editable: true, defaultOn: true, width: 70 },
  { key: 'geo', label: 'Координата', type: 'text', field: 'geo', editable: false, defaultOn: false, width: 170 }, // выдана при создании контейнера
  { key: 'accountType', label: 'Тип аккаунта', type: 'text', field: 'accountType', editable: true, defaultOn: true, width: 140 },
  { key: 'batch', label: 'Пачка', type: 'text', field: 'batch', editable: true, defaultOn: false, width: 160 },
  { key: 'creoName', label: 'Крео', type: 'text', field: 'creoName', editable: true, defaultOn: true, width: 130 },
  { key: 'cloneVersion', label: 'Версия клона', type: 'text', field: 'cloneVersion', editable: true, defaultOn: false, width: 120 },
  { key: 'warn', label: 'Варн', type: 'bool', field: 'warn', editable: true, defaultOn: true, width: 56 },
  { key: 'dayWorkout', label: 'Дневная отработка', type: 'text', field: 'dayWorkout', editable: false, defaultOn: true, width: 96 }, // от автопостера (ingest /workouts)
  { key: 'nightWorkout', label: 'Ночная отработка', type: 'text', field: 'nightWorkout', editable: false, defaultOn: true, width: 96 },
  { key: 'warn18', label: 'Варн 18+', type: 'bool', field: 'warn18', editable: true, defaultOn: true, width: 70 },
  { key: 'decorated', label: 'Оформление', type: 'bool', field: 'decorated', editable: true, defaultOn: true, width: 86 },
  { key: 'poured', label: 'Залито', type: 'bool', field: 'poured', editable: true, defaultOn: true, width: 62 },
  { key: 'link', label: 'Линк', type: 'text', field: 'link', editable: true, defaultOn: false, width: 190 },
  { key: 'startDate', label: 'Дата старта', type: 'date', field: 'startDate', editable: false, defaultOn: true, width: 100 }, // авто: ставится при переводе в «Прогрев»
  { key: 'banDate', label: 'Дата бана', type: 'date', field: 'banDate', editable: true, defaultOn: true, width: 100 },
  { key: 'viewsCategory', label: 'Категория просмотров', type: 'text', field: 'viewsCategory', editable: true, defaultOn: true, width: 190 },
  { key: 'views24h', label: 'Просм. 24ч', type: 'number', field: 'views24h', editable: true, defaultOn: true, width: 92 },
  { key: 'videos24h', label: 'Видео 24ч', type: 'number', field: 'videos24h', editable: true, defaultOn: false, width: 82 },
  { key: 'viewsTotal', label: 'Просм. всего', type: 'number', field: 'viewsTotal', editable: true, defaultOn: true, width: 104 },
  { key: 'containerReplacements', label: 'Замен в конт.', type: 'number', field: 'containerReplacements', editable: true, defaultOn: false, width: 92 },
  { key: 'creoReplaced', label: 'Замена крео', type: 'bool', field: 'creoReplaced', editable: true, defaultOn: false, width: 88 },
  { key: 'creoReplaceReason', label: 'Причина', type: 'text', field: 'creoReplaceReason', editable: true, defaultOn: false, width: 160 },
  { key: 'password', label: 'Пароль', type: 'secret', field: 'password', editable: true, adminOnly: true, defaultOn: false, width: 130 },
  { key: 'totpSecret', label: '2ФА ключ', type: 'secret', field: 'totpSecret', editable: true, adminOnly: true, defaultOn: false, width: 130 },
  { key: 'email', label: 'Почта', type: 'secret', field: 'email', editable: true, adminOnly: true, defaultOn: false, width: 150 },
  { key: 'emailPassword', label: 'Пароль почты', type: 'secret', field: 'emailPassword', editable: true, adminOnly: true, defaultOn: false, width: 130 },
  { key: 'deletedReels', label: 'Удалённых рилсов', type: 'number', field: 'deletedReels', editable: false, defaultOn: true, width: 96 },
  { key: 'postedToday', label: 'Рилсов сегодня', type: 'number', field: 'postedToday', editable: false, defaultOn: true, width: 90 }, // опубликовано сегодня (день Варшавы)
  { key: 'deletedToday', label: 'Удалено сегодня', type: 'number', field: 'deletedToday', editable: false, defaultOn: true, width: 92 }, // из сегодняшних рилсов снесено инстой
  { key: 'archivedAt', label: 'В архиве с', type: 'datetime', field: 'archivedAt', editable: false, defaultOn: false, onlyArchive: true, width: 120 },
  { key: 'lastParsedAt', label: 'Парсинг', type: 'date', field: 'lastParsedAt', editable: false, defaultOn: false, width: 90 },
];

function storageKey(userId: string, tableKey: string): string {
  return `wml-insta.cols.${userId}.${tableKey}`;
}

export function loadVisibleColumns(
  userId: string,
  tableKey: string,
  isAdmin: boolean,
  hiddenByDefault: string[] = [],
): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId, tableKey));
    if (raw) {
      const arr = JSON.parse(raw) as string[];
      if (Array.isArray(arr)) return new Set(arr.filter((k) => typeof k === 'string'));
    }
  } catch {
    /* localStorage недоступен / битые данные — падаем на дефолт */
  }
  return new Set(
    ACCOUNT_COLUMNS
      .filter((c) => (tableKey === 'archive' ? c.defaultOn || c.onlyArchive : c.defaultOn && !c.onlyArchive))
      .filter((c) => !(c.adminOnly && !isAdmin) && !hiddenByDefault.includes(c.key))
      .map((c) => c.key),
  );
}

export function saveVisibleColumns(userId: string, tableKey: string, cols: Set<string>): void {
  try {
    localStorage.setItem(storageKey(userId, tableKey), JSON.stringify(Array.from(cols)));
  } catch {
    /* тихо — настройка просто не сохранится */
  }
}

// ---- Ширины колонок (drag-resize), тоже свои на юзера+экран ----

function widthsKey(userId: string, tableKey: string): string {
  return `wml-insta.colw.${userId}.${tableKey}`;
}

export function loadColumnWidths(userId: string, tableKey: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(widthsKey(userId, tableKey));
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, unknown>;
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'number' && Number.isFinite(v)) out[k] = Math.max(50, Math.min(800, v));
      }
      return out;
    }
  } catch {
    /* дефолты */
  }
  return {};
}

export function saveColumnWidths(userId: string, tableKey: string, widths: Record<string, number>): void {
  try {
    localStorage.setItem(widthsKey(userId, tableKey), JSON.stringify(widths));
  } catch {
    /* тихо */
  }
}
